import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HD_LOVE_GATE_COLOR, HD_LOVE_FALLBACK_COLOR } from '@/app/human-design/chart/love/hd-love.metadata';

interface LoveCheatCardVm {
  readonly background: string;
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
}

/**
 * Poortkaart van de Love gates cheat-sheet-pagina, 1:1 naar het Figma-poster
 * (node 1732-8232): groot poortnummer linksboven, titel + keynote-ondertitel +
 * lopende tekst rechts uitgelijnd, met de centrumkleur als kaartachtergrond en
 * een scheidingslijn onder de kop. Leest zijn content zelf uit de
 * `human-design/love`-scope (taalwissel + NL-fallback).
 */
@Component({
  selector: 'hd-love-cheat-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hd-love-cheat-card',
    '[style.background]': 'vm().background',
    '[attr.data-gate]': 'gate()',
  },
  template: `
    <div class="hd-love-cheat-card__head">
      <span class="hd-love-cheat-card__gate">{{ gate() }}</span>
      <div class="hd-love-cheat-card__titles">
        <h3 class="hd-love-cheat-card__title">{{ vm().title }}</h3>
        <p class="hd-love-cheat-card__subtitle">{{ vm().subtitle }}</p>
      </div>
    </div>
    <p class="hd-love-cheat-card__description">{{ vm().description }}</p>
  `,
  styles: `
    :host {
      display: block;
      padding: 10px 12px 12px;
      border-radius: 6px;
      color: #3d414e;
    }

    .hd-love-cheat-card__head {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(61, 65, 78, 0.35);
    }

    .hd-love-cheat-card__gate {
      flex: 0 0 auto;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 30px;
      line-height: 1;
    }

    .hd-love-cheat-card__titles {
      flex: 1 1 auto;
      min-width: 0;
      margin-left: auto;
      text-align: right;
    }

    .hd-love-cheat-card__title {
      margin: 0;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 18px;
      line-height: 1.15;
    }

    .hd-love-cheat-card__subtitle {
      margin: 2px 0 0;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 700;
      font-size: 12.5px;
      line-height: 1.2;
    }

    .hd-love-cheat-card__description {
      margin: 8px 0 0;
      font-family: 'Raleway', sans-serif;
      font-size: 12px;
      line-height: 1.45;
      text-align: right;
    }
  `,
})
export class HdLoveCheatCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly gate = input.required<number>();

  protected readonly vm = computed<LoveCheatCardVm>(() => {
    const gate = this.gate();
    return {
      background: HD_LOVE_GATE_COLOR[gate] ?? HD_LOVE_FALLBACK_COLOR,
      title: this.text(`gates.${gate}.keynote`),
      subtitle: this.text(`gates.${gate}.subtitle`),
      description: this.text(`gates.${gate}.description`),
    };
  });

  constructor() {
    // Beide content-talen eager laden (idempotent) voor taalwissel + fallback.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('love', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('love', key, 'nl');
  }
}
